import { useEffect, useSyncExternalStore } from 'react';

type Listener = () => void;

const listeners = new Set<Listener>();

const serialize = (value: unknown) => JSON.stringify(value);
const deserialize = (value: string | null) => (value ? JSON.parse(value) : null);

const onStorageEvent = () => listeners.forEach((listener) => listener());

const subscribe = (listener: Listener) => {
  if (listeners.size === 0) {
    window.addEventListener('storage', onStorageEvent);
  }

  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('storage', onStorageEvent);
    }
  };
};

const getSnapshot = <T>(key: string) => {
  const value = localStorage.getItem(key);
  return deserialize(value) as T | null;
};

export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const value = useSyncExternalStore(subscribe, () => getSnapshot<T>(key) ?? initialValue);

  useEffect(() => {
    if (getSnapshot<T>(key) === null) {
      localStorage.setItem(key, serialize(initialValue));
    }
  }, [initialValue, key]);

  const setLocalStorage = (newValue: T) => {
    localStorage.setItem(key, serialize(newValue));
    listeners.forEach((listener) => listener());
  };

  return [value, setLocalStorage] as const;
};
